import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../Caches.module.css";
import LfuEngine, { LFUNode } from "../mapengine/LfuEngine";

const LfuWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [capacity, setCapacity] = useState(4);
  const [keyInput, setKeyInput] = useState("");
  const [valInput, setValInput] = useState("");
  const [getKey, setGetKey] = useState("");
  const [lastResult, setLastResult] = useState<string | null>(null);
  const {
    capacity: engineCapacity,
    setCapacity: setEngineCapacity,
    map,
    get,
    put,
    silentPut,
    reset,
    highlightNodeId,
    highlightFreq,
    animating,
  } = LfuEngine(capacity);

  const handleCapacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = Number(e.target.value);
    const newCap = isNaN(raw) || raw < 1 ? 1 : raw;
    setCapacity(newCap);
    reset(newCap);
    setLastResult(null);
  };

  const handleReset = () => {
    reset(capacity);
    setKeyInput("");
    setValInput("");
    setGetKey("");
    setLastResult(null);
  };

  const handlePut = () => {
    if (!keyInput || !valInput) return;
    put(keyInput, valInput);
    setLastResult(null);
  };

  const handleGet = () => {
    if (!getKey) return;
    const value = get(getKey);
    setLastResult(value === null ? `Miss: ${getKey}` : `Hit: ${value}`);
  };

  const handleGenerateRandom = () => {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    setKeyInput("");
    setValInput("");
    setGetKey("");
    setLastResult(null);
    reset(capacity);

    setTimeout(() => {
      const count = Math.min(capacity, alphabet.length);
      for (let i = 0; i < count; i++) {
        const key = alphabet[i];
        const value = String(Math.floor(Math.random() * 90) + 10);
        silentPut(key, value);
      }
    }, 0);
  };

  const mapNodes = Array.from(map.values());
  const freqGroups = useMemo(() => groupByFrequency(mapNodes), [mapNodes]);
  return (
    <div className={styles.cachePageContainer}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>← Return</button>

      <h1 className={styles.title}>LFU Cache (Least Frequently Used)</h1>
      <p className={styles.description}>
        The LFU (Least Frequently Used) Cache removes the item with the{" "}
        <strong>lowest access count</strong> when the cache is full.
        <br />
        It keeps the elements that are used the most often.
        <br />
        <br />
        <strong>Top: </strong> Hash Map (key → node)
        <br />
        <strong>Bottom: </strong> Frequency Buckets grouped by access count.
      </p>

      <div className={styles.useCase}>
        <h3>Use Cases</h3>
        <p>
          LFU is useful for workloads where items gain value with repeated use —
          recommendation results, expensive API responses, precomputed data, etc.
        </p>
      </div>

      <div className={styles.controlsBar}>
              <input
                type="number"
                value={capacity}
                min={1}
                onChange={handleCapacityChange}
                className={styles.controlInput}
              />
              <button className={styles.cacheButton}>Capacity</button>

              <input
                placeholder="key"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                className={styles.controlInput}
              />
              <input
                placeholder="value"
                value={valInput}
                onChange={(e) => setValInput(e.target.value)}
                className={styles.controlInput}
              />
              <button className={styles.cacheButton} onClick={handlePut}>
                Put
              </button>

              <input
                placeholder="key"
                value={getKey}
                onChange={(e) => setGetKey(e.target.value)}
                className={styles.controlInput}
              />
              <button className={styles.cacheButton} onClick={handleGet}>
                Get
              </button>

              <button className={styles.cacheButton} onClick={handleReset}>
                Reset
              </button>

              <button
                className={`${styles.cacheButton} ${styles.randomButton}`}
                onClick={handleGenerateRandom}
              >
                Random
              </button>
      </div>

      {lastResult && (
        <p style={{ textAlign: "center", marginTop: "0.5rem" }}>{lastResult}</p>
      )}

      <div className={styles.lruMainArea}>
        <div className={styles.sectionCard}>
          <h3>Hash Map (key → node)</h3>
          <div className={styles.arrayDisplay}>
            <span className={styles.arrayBracket}>[</span>
            <div className={styles.arrayItems}>
              {mapNodes.map((node, i) => (
                <div
                  key={node.id}
                  className={`${styles.arrayItem} ${
                    highlightNodeId === node.id ? styles.arrayItemActive : ""
                  }`}
                >
                  {`{ ${node.key}: ${node.value}, freq: ${node.freq} }`}
                  {i < mapNodes.length - 1 && (
                    <span className={styles.arrayComma}>,</span>
                  )}
                </div>
              ))}
            </div>
            <span className={styles.arrayBracket}>]</span>
          </div>
        </div>

        <div className={styles.sectionCard}>
          <h3>Frequency Buckets (LFU)</h3>

          <div className={styles.freqGroupsColumn}>
            {freqGroups.map(group => (
              <div
                key={group.freq}
                className={`${styles.freqGroupCard} ${
                  highlightFreq === group.freq ? styles.freqGroupActive : ""
                }`}
              >
                <div className={styles.freqHeader}>
                  freq = {group.freq}
                </div>

                <div className={styles.dllContainer}>

                  <div className={styles.dllEnd}>HEAD</div>
                  <div className={styles.dllArrow}>⇄</div>

                  {group.nodes.length === 0 && (
                    <div className={styles.emptyMsg}>empty</div>
                  )}

                  {group.nodes.map((node, idx) => (
                    <React.Fragment key={node.id}>
                      <div
                        className={`${styles.dllNode} ${
                          highlightNodeId === node.id ? styles.highlightNode : ""
                        }`}
                      >
                        <div className={styles.dllKey}>{node.key}</div>
                        <div className={styles.dllVal}>{node.value}</div>
                      </div>

                      {idx < group.nodes.length - 1 && (
                        <div className={styles.dllArrow}>⇄</div>
                      )}
                    </React.Fragment>
                  ))}

                  <div className={styles.dllArrow}>⇄</div>
                  <div className={styles.dllEnd}>TAIL</div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

function groupByFrequency(nodes: LFUNode[]) {
  const m = new Map<number, LFUNode[]>();
  for (const n of nodes) {
    if (!m.has(n.freq)) m.set(n.freq, []);
    m.get(n.freq)!.push(n);
  }
  return Array.from(m.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([freq, list]) => ({ freq, nodes: list }));
}

export default LfuWrapper;





